import type { ToolUseRenderBlock } from "@/lib/chat-render";

/** browser toolset のメンバー名 → 日本語の動作名。 */
const MEMBER_LABELS: Record<string, string> = {
	navigate: "ページを開く",
	screenshot: "スクリーンショット",
	zoom: "拡大表示",
	left_click: "クリック",
	right_click: "右クリック",
	middle_click: "中クリック",
	double_click: "ダブルクリック",
	triple_click: "トリプルクリック",
	hover: "ホバー",
	left_click_drag: "ドラッグ",
	left_mouse_down: "マウスボタンを押す",
	left_mouse_up: "マウスボタンを離す",
	mouse_move: "ポインタを移動",
	scroll: "スクロール",
	scroll_to: "要素までスクロール",
	type: "文字を入力",
	key: "キーを押す",
	hold_key: "キーを押し続ける",
	wait: "待機",
	read_page: "ページ構造を読む",
	find: "要素を探す",
	get_page_text: "ページ本文を読む",
	form_input: "フォームに入力",
	file_upload: "ファイルを添付",
	read_console: "コンソールを読む",
	read_network: "通信ログを読む",
	javascript_exec: "JavaScript を実行",
	new_tab: "新しいタブ",
	list_tabs: "タブ一覧",
	switch_tab: "タブを切り替え",
	close_tab: "タブを閉じる",
};

export function isBrowserTool(tool: Pick<ToolUseRenderBlock, "toolsetName">): boolean {
	return tool.toolsetName === "browser";
}

/**
 * メンバー名だけから日本語の動作名を引く。生成開始通知は toolset を運ばないため、名前で判定する
 * (メンバー名は既存ツール名と衝突しない)。未知なら undefined。
 */
export function browserMemberLabel(name: string): string | undefined {
	return MEMBER_LABELS[name];
}

function targetText(target: unknown): string {
	if (!target || typeof target !== "object") return "";
	const t = target as { type?: unknown; x?: unknown; y?: unknown };
	if (t.type === "coordinate" && typeof t.x === "number" && typeof t.y === "number") {
		return `(${t.x}, ${t.y})`;
	}
	return "";
}

/** 入力から補足 1 つを選ぶ (URL / 対象 / 入力文字列 / クエリ)。 */
function browserDetail(name: string, input: Record<string, unknown>): string {
	switch (name) {
		case "navigate":
			return typeof input.url === "string" ? input.url : "";
		case "type":
		case "key":
		case "hold_key":
			return typeof input.text === "string" ? input.text : "";
		case "find":
			return typeof input.query === "string" ? input.query : "";
		case "switch_tab":
		case "close_tab":
			return typeof input.tab_id === "string" ? input.tab_id : "";
		case "scroll":
			return typeof input.scroll_direction === "string" ? input.scroll_direction : "";
		case "wait":
			return typeof input.duration === "number" ? `${input.duration}秒` : "";
		case "form_input": {
			const target = targetText(input.target);
			const value = input.value === undefined ? "" : String(input.value);
			return target && value ? `${target} = ${value}` : target || value;
		}
		default:
			return targetText(input.target);
	}
}

/** browser toolset のツール 1 件を 1 行で言い表す。 */
export function browserToolLabel(tool: Pick<ToolUseRenderBlock, "name" | "input">): string {
	const label = MEMBER_LABELS[tool.name] ?? `ブラウザ (${tool.name})`;
	const detail = browserDetail(tool.name, tool.input);
	const trimmed = detail.length > 80 ? `${detail.slice(0, 79)}…` : detail;
	return trimmed ? `${label} (${trimmed})` : label;
}
